import { useEffect, useMemo, useState } from 'react';
import { CapabilityDetailLayout } from '@/components/capability-detail/CapabilityDetailLayout';
import { ToolHandlerTab } from '@/components/tools/ToolHandlerTab';
import { useFileRevisionEditor, type FileResolverArgs } from '@/hooks/use-file-revision-editor';
import type { CodeModuleLanguage } from '@/components/document-view';

const HANDLER_FILE_PATTERNS = [
    /handler\.[jt]sx?$/i,
    /handler\.py$/i,
    /handler\.go$/i,
    /index\.[jt]sx?$/i,
    /main\.[jt]sx?$/i,
    /main\.py$/i,
];

const EXT_TO_LANGUAGE: [RegExp, CodeModuleLanguage][] = [
    [/\.tsx$/i, 'tsx'],
    [/\.ts$/i, 'typescript'],
    [/\.jsx$/i, 'jsx'],
    [/\.js$/i, 'javascript'],
    [/\.mjs$/i, 'javascript'],
    [/\.cjs$/i, 'javascript'],
    [/\.py$/i, 'python'],
    [/\.json$/i, 'json'],
];

const LANGUAGE_TO_DEFAULT_FILE: Record<CodeModuleLanguage, string> = {
    javascript: 'handler.js',
    typescript: 'handler.ts',
    jsx: 'handler.jsx',
    tsx: 'handler.tsx',
    python: 'handler.py',
    json: 'handler.json',
    plain: 'handler.txt',
};

const LANGUAGE_TO_CONTENT_TYPE: Record<CodeModuleLanguage, string> = {
    javascript: 'application/javascript',
    typescript: 'application/typescript',
    jsx: 'application/javascript',
    tsx: 'application/typescript',
    python: 'text/x-python',
    json: 'application/json',
    plain: 'text/plain',
};

function languageFromPath(filePath: string): CodeModuleLanguage {
    for (const [re, lang] of EXT_TO_LANGUAGE) {
        if (re.test(filePath)) return lang;
    }
    return 'plain';
}

function resolveHandlerFilePath({ files, typeConfig }: FileResolverArgs): string {
  // Schema-consistent: read from implementation block
  const implementation = typeConfig?.implementation as
    | Record<string, unknown>
    | undefined;
  const entrypoint = implementation?.entrypoint;
  if (typeof entrypoint === "string" && entrypoint) {
    const match = files?.find(
      (f) => f.path === entrypoint || f.path.endsWith(entrypoint),
    );
    if (match) return match.path;
    return entrypoint;
  }

  // Fallback: first code file in revision
  if (files?.length) {
    for (const pattern of HANDLER_FILE_PATTERNS) {
      const match = files.find((f) => pattern.test(f.path));
      if (match) return match.path;
    }
    const codeFile = files.find(
      (f) => f.path.endsWith(".ts") || f.path.endsWith(".js") || f.path.endsWith(".py"),
    );
    if (codeFile) return codeFile.path;
    return files[0].path;
  }
  return "";
}

export interface ToolDetailPageProps {
    appKey?: string;
    name?: string;
    /** When provided, CapabilityDetailLayout pushes center + versionSelector to this header layer. */
    headerLayerId?: string;
}

export default function ToolDetailPage({
    appKey: appKeyProp,
    name: nameProp,
    headerLayerId,
}: ToolDetailPageProps = {}) {
    const [language, setLanguage] = useState<CodeModuleLanguage>('javascript');

    const editor = useFileRevisionEditor({
        resolveFilePath: resolveHandlerFilePath,
        primaryContentType: LANGUAGE_TO_CONTENT_TYPE[language] ?? 'text/plain',
        newFilePath: LANGUAGE_TO_DEFAULT_FILE[language] ?? 'handler.js',
        logPrefix: 'tool-detail-page',
        saveSuccessMessage: 'Handler saved',
        saveErrorMessage: 'Failed to save handler',
        appKey: appKeyProp,
        capabilityName: nameProp,
    });

    const detectedLanguage = useMemo(
        () => languageFromPath(editor.primaryFilePath),
        [editor.primaryFilePath],
    );

    useEffect(() => {
        setLanguage(detectedLanguage);
    }, [detectedLanguage]);

    return (
        <CapabilityDetailLayout
            typeLabel="Tool"
            appKey={appKeyProp}
            capabilityName={nameProp}
            supplementalDeferredSave={editor.supplementalDeferredSave}
            selectedRevisionId={editor.selectedRevisionId}
            onRevisionChange={editor.setSelectedRevisionId}
            headerLayerId={headerLayerId}
        >
            <ToolHandlerTab
                value={editor.displayedValue}
                onChange={editor.setDraft}
                language={editor.isViewingPublished ? detectedLanguage : language}
                onLanguageChange={setLanguage}
                isLoading={editor.displayedLoading}
                readOnly={editor.isArchived || editor.isViewingPublished || !editor.hasWorkingRevision}
            />
        </CapabilityDetailLayout>
    );
}
